import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { TemplateSummary } from '@/apis/logiblocksAPI';

/**
 * Redux slice for workflow templates.
 *
 * Templates are stored in Redux (unlike other Logiblocks state like workflows)
 * because they are static reference data that rarely changes. Storing them here
 * allows a single fetch on page load, avoiding refetches when components remount.
 */

export interface WorkflowTemplatesState {
  list: TemplateSummary[];
  loading: boolean;
  error: string | null;
}

export const fetchWorkflowTemplates = createAsyncThunk<
  TemplateSummary[],
  string,
  { rejectValue: string }
>(
  'workflowTemplates/fetchWorkflowTemplates',
  async (token: string, { rejectWithValue }) => {
    try {
      const { listTemplates } = await import('@/apis/logiblocksAPI');
      return await listTemplates(token);
    } catch {
      return rejectWithValue('Error fetching workflow templates');
    }
  }
);

const initialState: WorkflowTemplatesState = {
  list: [],
  loading: false,
  error: null,
};

const workflowTemplatesSlice = createSlice({
  name: 'workflowTemplates',
  initialState,
  reducers: {
    clearWorkflowTemplates: (state) => {
      state.list = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchWorkflowTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { clearWorkflowTemplates } = workflowTemplatesSlice.actions;

export const selectWorkflowTemplatesState = (state: {
  workflowTemplates: WorkflowTemplatesState;
}): WorkflowTemplatesState => state.workflowTemplates;

export const workflowTemplatesReducer = workflowTemplatesSlice.reducer;
